/**
 * Composant de panneau de filtres réutilisable
 */

import { LitElement, html, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { WidgetLanguage, FilterId } from "../types/widgets.d";
import { TranslationService } from "../services/TranslationService";
import { FilterGroup } from "../types/panel.d";
import filterPanelStyles from "../styles/filter-panel.css?inline";
import "./DatePicker";
import "./Combobox";
import type { DateRange } from "./DatePicker";
import type { ComboboxOption } from "./Combobox";

@customElement("apidae-filter-panel")
export class FilterPanel extends LitElement {
  static styles = unsafeCSS(filterPanelStyles);

  @property({ type: Array })
  filterGroups: FilterGroup[] = [];

  @property({ type: String })
  language: WidgetLanguage = WidgetLanguage.FR;

  @property({ type: Object })
  selectedValues: Map<FilterId, Set<string>> = new Map();

  @property({ type: Object })
  dateRange: DateRange = { dateDebut: null, dateFin: null };

  @state()
  private expandedGroups: Set<FilterId> = new Set();

  @state()
  private internalDateRange: DateRange = { dateDebut: null, dateFin: null };

  @state()
  private initialized: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    // Synchroniser l'état interne des dates avec la propriété dateRange
    this.internalDateRange = { ...this.dateRange };
    // Ouvrir tous les groupes par défaut à l'initialisation
    this.initializeExpandedGroups();
  }

  private initializeExpandedGroups() {
    if (!this.initialized && this.filterGroups.length > 0) {
      this.filterGroups.forEach((group) => {
        this.expandedGroups.add(group.id as FilterId);
      });
      this.initialized = true;
    }
  }

  updated(changedProperties: Map<string, any>) {
    // Synchroniser l'état interne des dates avec la propriété dateRange
    if (changedProperties.has("dateRange")) {
      this.internalDateRange = { ...this.dateRange };
    }
    // À l'initialisation, s'assurer que tous les groupes sont dépliés
    if (changedProperties.has("filterGroups") && !this.initialized) {
      this.initializeExpandedGroups();
    }
  }

  private toggleGroup(groupId: FilterId) {
    if (this.expandedGroups.has(groupId)) {
      this.expandedGroups.delete(groupId);
    } else {
      this.expandedGroups.add(groupId);
    }
    this.requestUpdate();
  }

  private toggleFilter(groupId: FilterId, optionId: string) {
    if (!this.selectedValues.has(groupId)) {
      this.selectedValues.set(groupId, new Set());
    }

    const selected = this.selectedValues.get(groupId)!;
    const group = this.filterGroups.find((g) => g.id === groupId);

    if (group?.multiple) {
      // Mode multiple: toggle
      if (selected.has(optionId)) {
        selected.delete(optionId);
      } else {
        selected.add(optionId);
      }
    } else {
      // Mode simple: remplacer
      if (selected.has(optionId)) {
        selected.clear();
      } else {
        selected.clear();
        selected.add(optionId);
      }
    }

    this.requestUpdate();
    this.emitFilterChange();
  }

  private resetFilters() {
    this.selectedValues.clear();
    this.internalDateRange = { dateDebut: null, dateFin: null };
    this.requestUpdate();
    this.emitFilterChange();
  }

  /**
   * Méthode publique pour réinitialiser les filtres depuis l'extérieur
   */
  public clearFilters() {
    this.resetFilters();
  }

  private emitFilterChange() {
    const filters: Map<FilterId, Set<string>> = new Map();
    this.selectedValues.forEach((values, key) => {
      filters.set(key, values);
    });

    // Ajouter les dates si définies, sinon s'assurer que FilterId.OUVERTURE n'est pas présent
    if (this.internalDateRange.dateDebut || this.internalDateRange.dateFin) {
      filters.set(
        FilterId.OUVERTURE,
        new Set([
          this.internalDateRange.dateDebut || "",
          this.internalDateRange.dateFin || "",
        ]),
      );
    } else {
      // Supprimer explicitement FilterId.OUVERTURE si les dates sont effacées
      filters.delete(FilterId.OUVERTURE);
    }

    this.dispatchEvent(
      new CustomEvent("filter-change", {
        detail: { filters, dateRange: this.internalDateRange },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private isFilterSelected(groupId: FilterId, optionId: string): boolean {
    return this.selectedValues.get(groupId)?.has(optionId) || false;
  }

  private t(key: string): string {
    return TranslationService.translate(key, this.language);
  }

  render() {
    return html`
      <div class="filter-header">
        <span class="filter-by-label">${this.t("filterBy")}</span>
      </div>

      <div class="filter-groups-container">
        ${this.filterGroups.map((group) => {
          // Filtre OUVERTURE : DatePicker
          if (group.id === FilterId.OUVERTURE) {
            return html`
              <div class="filter-group">
                <div
                  class="filter-group-header"
                  @click=${() => this.toggleGroup(group.id)}
                >
                  <span class="filter-group-label">${group.label}</span>
                  <span
                    class="filter-group-toggle ${this.expandedGroups.has(
                      group.id,
                    )
                      ? "expanded"
                      : ""}"
                    >></span
                  >
                </div>
                <div
                  class="filter-group-options ${this.expandedGroups.has(
                    group.id,
                  )
                    ? "expanded"
                    : ""}"
                >
                  <apidae-date-picker
                    .value=${this.internalDateRange}
                    .language=${this.language}
                    @date-change=${(e: CustomEvent) => {
                      this.internalDateRange = e.detail;
                      this.emitFilterChange();
                    }}
                  ></apidae-date-picker>
                </div>
              </div>
            `;
          }

          // Filtre COMMUNES : Combobox
          if (group.id === FilterId.COMMUNES) {
            const comboboxOptions: ComboboxOption[] = group.options.map(
              (opt) => ({
                id: opt.id,
                label: opt.label,
                count: opt.count,
              }),
            );
            const selectedCommuneIds = this.selectedValues.get(group.id)
              ? Array.from(this.selectedValues.get(group.id)!)
              : [];

            return html`
              <div class="filter-group">
                <div
                  class="filter-group-header"
                  @click=${() => this.toggleGroup(group.id)}
                >
                  <span class="filter-group-label">${group.label}</span>
                  <span
                    class="filter-group-toggle ${this.expandedGroups.has(
                      group.id,
                    )
                      ? "expanded"
                      : ""}"
                    >></span
                  >
                </div>
                <div
                  class="filter-group-options ${this.expandedGroups.has(
                    group.id,
                  )
                    ? "expanded"
                    : ""}"
                >
                  <apidae-combobox
                    .options=${comboboxOptions}
                    .selectedValues=${selectedCommuneIds}
                    .language=${this.language}
                    @selection-change=${(e: CustomEvent) => {
                      const selected = new Set<string>(e.detail.selectedValues);
                      this.selectedValues.set(group.id, selected);
                      this.requestUpdate();
                      this.emitFilterChange();
                    }}
                  ></apidae-combobox>
                </div>
              </div>
            `;
          }

          // Filtres normaux : checkboxes
          return html`
            <div
              class="filter-group ${group.options.length > 0
                ? ""
                : "group-hidden"}"
            >
              <div
                class="filter-group-header"
                @click=${() => this.toggleGroup(group.id)}
              >
                <span class="filter-group-label">${group.label}</span>
                <span
                  class="filter-group-toggle ${this.expandedGroups.has(group.id)
                    ? "expanded"
                    : ""}"
                  >></span
                >
              </div>

              <div
                class="filter-group-options ${this.expandedGroups.has(group.id)
                  ? "expanded"
                  : ""}"
              >
                ${group.options.map(
                  (option) => html`
                    <div
                      class="filter-option"
                      @click=${() => this.toggleFilter(group.id, option.id)}
                    >
                      <div
                        class="filter-checkbox ${this.isFilterSelected(
                          group.id,
                          option.id,
                        )
                          ? "checked"
                          : ""}"
                      ></div>
                      <span class="filter-option-label">${option.label}</span>
                      ${option.count !== undefined
                        ? html`
                            <span class="filter-option-count"
                              >${option.count}</span
                            >
                          `
                        : ""}
                    </div>
                  `,
                )}
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-filter-panel": FilterPanel;
  }
}
